/**
 * HrDashboardPage 컴포넌트 (V62 개편)
 *
 * HR 대시보드 — 건수 중심 경량 대시보드
 *
 * **구조:**
 * - 6개 섹션: 고용관리 / 인사발령 / 전배관리 / 승인관리(휴가/휴직) / 인사평가현황 / 증명서관리
 * - 각 항목은 건수만 표시, 클릭 시 해당 관리 페이지로 이동
 *
 * **데이터:**
 * - 단일 CF `getHrDashboardCounts`로 모든 건수를 한 번에 조회
 *
 * **권한:**
 * - hr.manage 권한 필요 (HR, Super Admin만 접근 가능)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Loader,
  AlertCircle,
  ChevronRight,
  Briefcase,
  Award,
  ArrowRightLeft,
  CalendarDays,
  ClipboardCheck,
  FileCheck,
  AlertTriangle,
  RefreshCw,
  Users,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';

// ============================================
// 타입 정의
// ============================================

interface DashboardCounts {
  employment: {
    expiringNoReservation: number;
    contractExtension: number;
    terminationPlanned: number;
    typeChangePlanned: number;
  };
  appointment: {
    promotionCandidates: number;
    promotion: number;
    earlyPromotion: number;
    positionAppointment: number;
    titleAppointment: number;
  };
  transferPending: number;
  leavePending: number;
  leaveOfAbsencePending: number;
  evalPendingHr: number;
  certPending: number;
}

interface CountItemProps {
  label: string;
  count: number;
  unit?: string;
  onClick: () => void;
  warning?: boolean;
}

// ============================================
// 서브 컴포넌트
// ============================================

function CountItem({ label, count, unit = '명', onClick, warning }: CountItemProps) {
  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        width: '100%',
        padding: '12px 16px',
        background: warning && count > 0 ? '#fffbeb' : 'white',
        border: warning && count > 0 ? '1px solid #fde68a' : '1px solid #f3f4f6',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'all 0.15s',
        fontSize: '14px',
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.backgroundColor = '#f9fafb';
        e.currentTarget.style.borderColor = '#d1d5db';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.backgroundColor = warning && count > 0 ? '#fffbeb' : 'white';
        e.currentTarget.style.borderColor = warning && count > 0 ? '#fde68a' : '#f3f4f6';
      }}
    >
      <span style={{ color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
        {warning && count > 0 && <AlertTriangle size={14} color="#f59e0b" />}
        {label}
      </span>
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
        <span style={{
          fontSize: '18px',
          fontWeight: '700',
          color: count > 0 ? '#1f2937' : '#9ca3af',
        }}>
          {count}
        </span>
        <span style={{ fontSize: '13px', color: '#6b7280' }}>{unit}</span>
        <ChevronRight size={16} color="#9ca3af" />
      </span>
    </button>
  );
}

function SectionCard({
  icon,
  title,
  color,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  color: string;
  children: React.ReactNode;
}) {
  return (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '12px',
      border: '1px solid #e5e7eb',
      boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
      overflow: 'hidden',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '16px 20px',
        borderBottom: '1px solid #f3f4f6',
      }}>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '36px',
          height: '36px',
          borderRadius: '8px',
          backgroundColor: color + '15',
        }}>
          {icon}
        </div>
        <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
          {title}
        </h3>
      </div>
      <div style={{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {children}
      </div>
    </div>
  );
}

function SubSectionLabel({ label }: { label: string }) {
  return (
    <div style={{
      fontSize: '12px',
      fontWeight: '600',
      color: '#9ca3af',
      textTransform: 'uppercase' as const,
      letterSpacing: '0.05em',
      padding: '8px 0 2px 4px',
    }}>
      {label}
    </div>
  );
}

// ============================================
// V66: 인력 구성 현황 카드
// ============================================

interface CompositionRow {
  code: 'REGULAR' | 'CONTRACT' | 'INTERN' | 'DISPATCH';
  label: string;
  count: number;
  male: number;
  female: number;
  other: number;
}
interface CompositionResponse {
  success: boolean;
  referenceDate: string;
  compositions: CompositionRow[];
  total: { count: number; male: number; female: number; other: number };
}

function WorkforceCompositionCard() {
  const todayKst = (() => {
    const now = new Date();
    const kst = new Date(now.getTime() + 9 * 60 * 60 * 1000);
    return kst.toISOString().split('T')[0];
  })();

  const [refDate, setRefDate] = useState<string>(todayKst);
  const [data, setData] = useState<CompositionResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // V66 (재추적): mount 시점 진단 로그 — 사용자 브라우저 콘솔에 항상 출력되어
  //   "system_admin 외 계정에서 안 보임" 재현 시 즉시 분기 추적 가능
  useEffect(() => {
    console.log('[V66 Card MOUNT]', {
      timestamp: new Date().toISOString(),
      todayKst,
      functionsAvailable: !!functions,
      buildVersion: 'V66-trace-' + new Date().toISOString().slice(0, 10),
    });
  }, []);

  useEffect(() => {
    if (!functions) {
      console.warn('[V66 Card] functions 객체 없음 → callable 호출 불가');
      return;
    }
    let abort = false;
    let settled = false;
    const fetch = async () => {
      const _t0 = Date.now();
      const timeoutHandle = setTimeout(() => {
        if (!settled && !abort) {
          console.warn('[V66 Card] callable timeout 의심 — 15s 초과, 응답/에러 없음', {
            refDate,
            elapsedMs: Date.now() - _t0,
          });
        }
      }, 15000);
      try {
        setLoading(true);
        setErr(null);
        console.log('[V66 Card] callable 호출 시작', { refDate });
        const fn = httpsCallable<{ referenceDate?: string }, CompositionResponse>(
          functions!,
          'getPersonnelCompositionStats'
        );
        const r = await fn({ referenceDate: refDate });
        settled = true;
        clearTimeout(timeoutHandle);
        if (abort) return;
        console.log('[V66 Card] callable 응답 OK', {
          referenceDate: r.data.referenceDate,
          totalCount: r.data.total?.count,
          rows: r.data.compositions?.length,
          elapsedMs: Date.now() - _t0,
          compositions: r.data.compositions,
        });
        setData(r.data);
      } catch (e: any) {
        settled = true;
        clearTimeout(timeoutHandle);
        if (!abort) {
          console.error('[V66 Card] callable 실패', {
            code: e?.code,
            message: e?.message,
            details: e?.details,
            elapsedMs: Date.now() - _t0,
            full: e,
          });
          setErr(e?.message || '조회 실패');
        }
      } finally {
        if (!abort) setLoading(false);
      }
    };
    fetch();
    return () => { abort = true; };
  }, [refDate]);

  const visibleRows = (data?.compositions || []).filter(r => r.count > 0);
  const total = data?.total || { count: 0, male: 0, female: 0, other: 0 };

  // V66 UI: 표 컬럼 정의 — 마지막 컬럼은 세로 구분선 제외
  const COL_COUNT = 5;

  return (
    // V66 UI: 기존 SectionCard 와 동일 sizing — 외부 grid(3 col) 안에서 1셀 차지
    <div
      style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        border: '1px solid #e5e7eb',
        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        overflow: 'hidden',
      }}
    >
      {/* 헤더 — SectionCard 패턴 + 우측 DatePicker */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '10px',
          padding: '16px 20px',
          borderBottom: '1px solid #f3f4f6',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '36px',
              height: '36px',
              borderRadius: '8px',
              backgroundColor: '#10b98115',
            }}
          >
            <Users size={20} color="#10b981" />
          </div>
          {/* V66 UI: 제목 폰트 확대 (15 → 18) */}
          <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
            인력 구성 현황
          </h3>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <label style={{ fontSize: '12px', color: '#6b7280' }}>기준일</label>
          <input
            type="date"
            value={refDate}
            onChange={(e) => setRefDate(e.target.value)}
            style={{
              padding: '4px 8px',
              border: '1px solid #d1d5db',
              borderRadius: '6px',
              fontSize: '13px',
            }}
          />
        </div>
      </div>

      {/* 본문 */}
      <div style={{ padding: '12px 16px' }}>
        {loading ? (
          <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
            조회 중...
          </div>
        ) : err ? (
          <div style={{ padding: '12px', color: '#dc2626', fontSize: '14px' }}>{err}</div>
        ) : visibleRows.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', color: '#9ca3af', fontSize: '14px' }}>
            데이터가 없습니다.
          </div>
        ) : (
          // V66 UI: 표 폰트 확대 (13 → 15) + 세로 구분선
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '15px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                <Th colIndex={0} colCount={COL_COUNT}>구분</Th>
                <Th colIndex={1} colCount={COL_COUNT} align="right">비율</Th>
                <Th colIndex={2} colCount={COL_COUNT} align="right">인원</Th>
                <Th colIndex={3} colCount={COL_COUNT} align="right">남</Th>
                <Th colIndex={4} colCount={COL_COUNT} align="right">여</Th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.map((r) => {
                const ratio = total.count > 0
                  ? `${((r.count / total.count) * 100).toFixed(1)}%`
                  : '-';
                return (
                  <tr key={r.code} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <Td colIndex={0} colCount={COL_COUNT}>{r.label}</Td>
                    <Td colIndex={1} colCount={COL_COUNT} align="right">{ratio}</Td>
                    <Td colIndex={2} colCount={COL_COUNT} align="right" style={{ fontWeight: 600 }}>{r.count}</Td>
                    <Td colIndex={3} colCount={COL_COUNT} align="right">{r.male}</Td>
                    <Td colIndex={4} colCount={COL_COUNT} align="right">{r.female}</Td>
                  </tr>
                );
              })}
              <tr style={{ backgroundColor: '#f9fafb', borderTop: '2px solid #e5e7eb' }}>
                <Td colIndex={0} colCount={COL_COUNT} style={{ fontWeight: 700 }}>합계</Td>
                <Td colIndex={1} colCount={COL_COUNT} align="right" style={{ color: '#9ca3af' }}>-</Td>
                <Td colIndex={2} colCount={COL_COUNT} align="right" style={{ fontWeight: 700 }}>{total.count}</Td>
                <Td colIndex={3} colCount={COL_COUNT} align="right" style={{ fontWeight: 700 }}>{total.male}</Td>
                <Td colIndex={4} colCount={COL_COUNT} align="right" style={{ fontWeight: 700 }}>{total.female}</Td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// V66 UI: 세로 구분선 — 마지막 컬럼은 border-right 제외
function Th({
  children,
  align,
  colIndex,
  colCount,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  colIndex: number;
  colCount: number;
}) {
  const isLast = colIndex === colCount - 1;
  return (
    <th
      style={{
        padding: '10px 12px',
        textAlign: align || 'left',
        fontSize: '14px',
        fontWeight: 600,
        color: '#6b7280',
        borderRight: isLast ? 'none' : '1px solid #e5e7eb',
      }}
    >
      {children}
    </th>
  );
}
function Td({
  children,
  align,
  style,
  colIndex,
  colCount,
}: {
  children: React.ReactNode;
  align?: 'left' | 'right' | 'center';
  style?: React.CSSProperties;
  colIndex: number;
  colCount: number;
}) {
  const isLast = colIndex === colCount - 1;
  return (
    <td
      style={{
        padding: '10px 12px',
        textAlign: align || 'left',
        color: '#1f2937',
        borderRight: isLast ? 'none' : '1px solid #f3f4f6',
        ...style,
      }}
    >
      {children}
    </td>
  );
}

// ============================================
// 메인 컴포넌트
// ============================================

export function HrDashboardPage() {
  const navigate = useNavigate();
  const [counts, setCounts] = useState<DashboardCounts | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchCounts = useCallback(async () => {
    if (!functions) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const fn = httpsCallable<Record<string, never>, { success: boolean; counts: DashboardCounts }>(
        functions,
        'getHrDashboardCounts'
      );
      const result = await fn({});
      if (result.data.success) {
        setCounts(result.data.counts);
      } else {
        throw new Error('대시보드 데이터 조회 실패');
      }
    } catch (err: any) {
      console.error('[HrDashboard] fetch error:', err);
      setError(err instanceof Error ? err : new Error(err.message || 'Unknown error'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCounts();
  }, [fetchCounts]);

  // 오늘 날짜 포맷
  const today = new Date();
  const formattedDate = `${today.getFullYear()}년 ${today.getMonth() + 1}월 ${today.getDate()}일`;

  // 로딩 상태
  if (isLoading) {
    return (
      <div style={styles.container}>
        <div style={styles.loadingContainer}>
          <Loader className="animate-spin" size={32} color="#6b7280" />
          <p style={styles.loadingText}>대시보드 데이터를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  // 에러 상태
  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorContainer}>
          <AlertCircle size={24} color="#dc2626" />
          <div>
            <p style={styles.errorTitle}>데이터 조회 실패</p>
            <p style={styles.errorMessage}>{error.message}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!counts) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyContainer}>
          <p>대시보드 데이터가 없습니다.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* 헤더 */}
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>
            인사팀 대시보드
          </h1>
          <p style={styles.subtitle}>처리 필요한 업무 건수를 한눈에 확인하세요</p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={fetchCounts}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '8px 14px',
              fontSize: '13px',
              color: '#6b7280',
              backgroundColor: 'white',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              cursor: 'pointer',
            }}
          >
            <RefreshCw size={14} />
            새로고침
          </button>
          <div style={styles.dateLabel}>{formattedDate}</div>
        </div>
      </div>

      {/* 건수 카드 그리드 */}
      <div style={styles.grid}>
        {/* ── 고용관리 ── */}
        <SectionCard
          icon={<Briefcase size={20} color="#3b82f6" />}
          title="고용관리"
          color="#3b82f6"
        >
          <CountItem
            label="계약만료 임박 (미예약)"
            count={counts.employment.expiringNoReservation}
            onClick={() => navigate('/hr/employment-management')}
            warning
          />
          <SubSectionLabel label="예약정보" />
          <CountItem
            label="계약연장"
            count={counts.employment.contractExtension}
            onClick={() => navigate('/hr/employment-management?tab=reservations')}
          />
          <CountItem
            label="퇴사예정"
            count={counts.employment.terminationPlanned}
            onClick={() => navigate('/hr/employment-management?tab=reservations')}
          />
          <CountItem
            label="고용형태 변경"
            count={counts.employment.typeChangePlanned}
            onClick={() => navigate('/hr/employment-management?tab=reservations')}
          />
        </SectionCard>

        {/* ── 인사발령 ── */}
        <SectionCard
          icon={<Award size={20} color="#f59e0b" />}
          title="인사발령"
          color="#f59e0b"
        >
          <CountItem
            label="진급가능 후보"
            count={counts.appointment.promotionCandidates}
            onClick={() => navigate('/hr/personnel-appointment?tab=promotion-candidates')}
          />
          <SubSectionLabel label="예약정보" />
          <CountItem
            label="승진"
            count={counts.appointment.promotion}
            onClick={() => navigate('/hr/personnel-appointment?tab=reservation')}
          />
          <CountItem
            label="조기진급"
            count={counts.appointment.earlyPromotion}
            onClick={() => navigate('/hr/personnel-appointment?tab=reservation')}
          />
          <CountItem
            label="직위임명"
            count={counts.appointment.positionAppointment}
            onClick={() => navigate('/hr/personnel-appointment?tab=reservation')}
          />
          <CountItem
            label="직책임명"
            count={counts.appointment.titleAppointment}
            onClick={() => navigate('/hr/personnel-appointment?tab=reservation')}
          />
        </SectionCard>

        {/* ── 전배관리 ── */}
        <SectionCard
          icon={<ArrowRightLeft size={20} color="#8b5cf6" />}
          title="전배관리"
          color="#8b5cf6"
        >
          <CountItem
            label="전배 승인대기"
            count={counts.transferPending}
            unit="건"
            onClick={() => navigate('/hr/transfers')}
            warning
          />
        </SectionCard>

        {/* ── V67: 승인관리 (휴가/휴직 분리) ── */}
        <SectionCard
          icon={<CalendarDays size={20} color="#10b981" />}
          title="승인관리"
          color="#10b981"
        >
          <CountItem
            label="휴가 승인대기"
            count={counts.leavePending}
            unit="건"
            onClick={() => navigate('/hr/leave-approvals')}
            warning
          />
          <CountItem
            label="휴직 승인대기"
            count={counts.leaveOfAbsencePending}
            unit="건"
            onClick={() => navigate('/hr/leave-approvals')}
            warning
          />
        </SectionCard>

        {/* ── 인사평가현황 ── */}
        <SectionCard
          icon={<ClipboardCheck size={20} color="#ef4444" />}
          title="인사평가현황"
          color="#ef4444"
        >
          <CountItem
            label="평가서 인사팀 승인대기"
            count={counts.evalPendingHr}
            unit="건"
            onClick={() => navigate('/hr/performance')}
            warning
          />
        </SectionCard>

        {/* ── 증명서관리 ── */}
        <SectionCard
          icon={<FileCheck size={20} color="#6366f1" />}
          title="증명서관리"
          color="#6366f1"
        >
          <CountItem
            label="증명서 승인대기"
            count={counts.certPending}
            unit="건"
            onClick={() => navigate('/hr/certificate-management')}
            warning
          />
        </SectionCard>

        {/* V66: 인력 구성 현황 — 기존 grid 안에서 1셀 차지 (다른 카드와 동일 width) */}
        <WorkforceCompositionCard />
      </div>
    </div>
  );
}

// ============================================
// 스타일
// ============================================

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '24px',
    maxWidth: '1400px',
    margin: '0 auto',
    backgroundColor: '#f9fafb',
    minHeight: '100vh',
  },

  // 헤더
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
  },
  title: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '28px',
    fontWeight: 'bold',
    color: '#1f2937',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0,
  },
  dateLabel: {
    fontSize: '14px',
    color: '#6b7280',
    backgroundColor: 'white',
    padding: '8px 16px',
    borderRadius: '8px',
    border: '1px solid #e5e7eb',
  },

  // 로딩/에러/빈 상태
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    padding: '80px 0',
    gap: '16px',
  },
  loadingText: {
    fontSize: '14px',
    color: '#6b7280',
  },
  errorContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    padding: '16px',
    backgroundColor: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
  },
  errorTitle: {
    fontWeight: '600',
    color: '#dc2626',
    margin: '0 0 4px 0',
  },
  errorMessage: {
    fontSize: '14px',
    color: '#b91c1c',
    margin: 0,
  },
  emptyContainer: {
    textAlign: 'center' as const,
    padding: '40px',
    color: '#6b7280',
  },

  // 카드 그리드
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '16px',
  },
};
